import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta, { buildFaqPageSchema } from './PageMeta';
import { OWNER_SHARE_PERCENT, PLATFORM_FEE_PERCENT } from './rentalPayments';

function buildFaqItems() {
  return [
    {
      id: 'payments',
      question: 'How do payments work?',
      answer: [
        'After an owner approves your rental request, you save a payment method on the platform to secure the booking.',
        'Your card is charged 48 hours before the rental start date. If you book inside that window, the charge may process sooner.',
        'Payments are processed through Stripe. ATTACK! Your Adventure does not ask you to send money off-platform.',
      ],
    },
    {
      id: 'off-platform',
      question: 'Can I pay the owner directly?',
      answer: [
        'No. Keep all rental payments on ATTACK! Your Adventure.',
        'If someone asks you to use Venmo, Cash App, Zelle, PayPal Friends & Family, or cash instead of the platform, decline and contact us through the Contact page.',
        'Paying through the site is what connects your booking, messaging, and payment protections to the rental.',
      ],
    },
    {
      id: 'cancellations',
      question: 'How do cancellations and refunds work?',
      answer: [
        'You can cancel any time before a booking starts. What happens to the money depends on when you cancel, and whether you are the renter or the owner.',
        'More than 48 hours before the rental start: you can cancel without being charged (or with a full automatic refund if already charged). Any condition hold is released and the dates open back up.',
        'Within 48 hours, if the renter cancels: the card has already been charged and the booking is not refunded — the owner keeps the payment. The dates are released so the owner can try to re-rent them. If you and the owner agree on a refund, email admin@attackyouradventure.com and we will honor it.',
        'Within 48 hours, if the owner cancels: the renter is refunded in full automatically, and that amount is reversed out of the owner\'s payout. Those dates stay blocked on the owner\'s calendar so they cannot be re-rented to someone else.',
        'When you cancel, the site shows the refund message that applies to your situation before you confirm.',
      ],
    },
    {
      id: 'condition-holds',
      question: 'What is a condition guarantee hold?',
      answer: [
        'On eligible listings, a separate card authorization (up to $2,500) may be placed after the rental charge. It is not an extra rental fee.',
        'The hold is not captured unless gear is returned in unacceptable condition under the platform\'s damage process.',
        'The gear owner releases the hold after return when gear is in acceptable condition. Admins can assist when needed.',
      ],
    },
    {
      id: 'vehicles',
      question: 'What about vehicles, RVs, UTVs/ATVs, or aviation gear?',
      answer: [
        'ATTACK! Your Adventure is a marketplace platform. We are not responsible for damage to high-value equipment or its condition during a rental.',
        'Renters and owners should arrange appropriate insurance coverage directly with one another.',
        'For those categories, owners should set the condition guarantee hold to align with the applicable insurance deductible. You will be asked to acknowledge this notice when listing or renting in those categories.',
      ],
    },
    {
      id: 'reviews',
      question: 'How do reviews work?',
      answer: [
        'After eligible completed rentals, renters and owners may leave reviews through My Bookings.',
        'Reviews from renters appear on gear listings and help other members make informed decisions.',
        'If you believe a review is inaccurate or unfair, use the report option on the review or contact us privately.',
      ],
    },
    {
      id: 'owners',
      question: 'How do owners get paid?',
      answer: [
        'Owners connect Stripe on their profile to receive payouts. If Stripe is not connected when a rental is charged, your share is held until onboarding is complete.',
        `A renter's card is charged 48 hours before the rental start date (or immediately if the booking is made inside that window). When that charge processes, ATTACK! Your Adventure keeps a ${PLATFORM_FEE_PERCENT}% platform fee and transfers ${OWNER_SHARE_PERCENT}% to your connected Stripe account. That fee is intentionally minimal — and we plan to keep it that way.`,
        'The transfer to Stripe usually happens around the same time as the renter\'s charge — about two days before the rental starts, not after the gear is returned. You can see the scheduled date on My Bookings.',
        'After the transfer reaches your Stripe balance, Stripe deposits funds to your bank on its own payout schedule. For many new U.S. accounts this is about two business days; established accounts may receive daily payouts. Check your Stripe Dashboard for exact timing.',
        'Owners approve requests, coordinate pickup and return through Messaging, and release condition holds after gear is returned in acceptable condition.',
      ],
    },
  ];
}

function FaqPage() {
  const faqItems = buildFaqItems();
  const [openId, setOpenId] = useState(faqItems[0]?.id || null);

  return (
    <>
      <PageMeta
        title="FAQ"
        description={`Common questions about renting and listing gear on ATTACK! Your Adventure — payments, the ${PLATFORM_FEE_PERCENT}% platform fee, cancellations, condition holds, and more.`}
        path="/faq"
        jsonLd={buildFaqPageSchema(faqItems)}
      />
      <div className="faq-page">
        <h1>Frequently asked questions</h1>
        <p className="faq-lead">
          Straight answers about how ATTACK! Your Adventure works. For full legal terms, see{' '}
          <Link to="/rules">Rules</Link>, <Link to="/terms">Terms</Link>, and{' '}
          <Link to="/privacy">Privacy</Link>.
        </p>

        <div className="faq-list">
          {faqItems.map((item) => {
            const isOpen = openId === item.id;
            return (
              <article key={item.id} id={item.id} className="faq-item">
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={isOpen}
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                >
                  <span>{item.question}</span>
                  <span className="faq-toggle" aria-hidden="true">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                {isOpen && (
                  <div className="faq-answer">
                    {item.answer.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <p className="faq-footer-note">
          Still have questions?{' '}
          <Link to="/contact">Contact us</Link> or message an owner from any gear listing.
        </p>
      </div>
    </>
  );
}

export default FaqPage;
